/* Desktop and phone notifications for both apps.

   With the browser's permission and the API's VAPID key, this device subscribes to Web Push:
   the app's service worker (public/sw.js) shows each notification while no tab is in front, even
   with the app closed. An open tab that isn't in front (hidden, or another window focused) shows
   it itself only when push doesn't cover this device (no key, no service worker in development,
   an old browser, not subscribed), so each device shows a notification once (UX-191). Chrome
   doesn't let a tab's own Notification and the service worker's replace each other by tag, so
   both used to pop up. Now the tab asks the browser whether it holds a push subscription, shows
   through the service worker's registration when there is one (same tag as a push, so repeats
   replace), skips a tag that's already up, and only one of several open tabs shows it. The
   "Desktop notifications" switch in Settings turns both off for this device. iPhones and iPads
   only get either once the app is on the Home Screen. */

import type { Api } from "./api/client";

export type PushSupport =
  | "push" // Web Push: works with the app closed
  | "notify" // only while a tab is open
  | "ios-install" // iOS in a Safari tab: add to the Home Screen first
  | "none";

const PROMPT_KEY = "corecrm.notifications.prompt";
const DESKTOP_KEY = "corecrm.notifications.desktop";
const REMIND_AFTER_MS = 14 * 24 * 60 * 60 * 1000;
/** Ids of notifications one of this app's tabs already took on this device (UX-191). */
const SHOWN_KEY = "corecrm.notifications.shown";
const SHOWN_KEEP = 100;

let pushActive = false;

/** Whether the last sync subscribed this device to push (the settings switch says so). Showing a
    notification asks the browser itself instead: ``pushCoversDevice``. */
export function isPushActive() {
  return pushActive;
}

export function isIos() {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isInstalled() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** A phone or tablet: the prompt says "push notifications" rather than "desktop". */
export function isMobile() {
  return isIos() || /Android/i.test(navigator.userAgent);
}

export function pushSupport(): PushSupport {
  const notify = typeof window !== "undefined" && "Notification" in window;
  if (notify && "serviceWorker" in navigator && "PushManager" in window) return "push";
  if (isIos() && !isInstalled()) return "ios-install";
  return notify ? "notify" : "none";
}

export function permission(): NotificationPermission | "unsupported" {
  return typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported";
}

/** The person is looking at this tab: it's visible and its window has the focus. Then the
    in-app toast says it; otherwise a desktop notification does. */
export function appInFront() {
  if (typeof document === "undefined" || document.visibilityState !== "visible") return false;
  return typeof document.hasFocus !== "function" || document.hasFocus();
}

/** The "Desktop notifications" switch for this device (on unless turned off here). */
export function desktopEnabled() {
  try {
    return localStorage.getItem(DESKTOP_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setDesktopEnabled(on: boolean) {
  try {
    if (on) localStorage.removeItem(DESKTOP_KEY);
    else localStorage.setItem(DESKTOP_KEY, "off");
  } catch {
    /* no storage */
  }
}

/** Ask this device once (then again a fortnight after "Not now") until it's decided. */
export function shouldPrompt(now = Date.now()) {
  const support = pushSupport();
  if (support === "none") return false;
  if (support !== "ios-install" && permission() !== "default") return false;
  try {
    const seen = localStorage.getItem(PROMPT_KEY);
    if (seen === "done") return false;
    if (seen && now - Number(seen) < REMIND_AFTER_MS) return false;
  } catch {
    /* no storage (private window): ask */
  }
  return true;
}

export function rememberPrompt(choice: "done" | "later", now = Date.now()) {
  try {
    localStorage.setItem(PROMPT_KEY, choice === "done" ? "done" : String(now));
  } catch {
    /* no storage */
  }
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** The service worker, once it's running; null in development (none is registered) or after 10 s. */
async function worker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
  ]);
}

/** With permission granted, make sure this device is subscribed and the API knows it. Safe to
    call on every load: it's how a new sign-in on a browser takes over its subscription. */
export async function syncPush(api: Api): Promise<boolean> {
  if (pushSupport() !== "push" || permission() !== "granted" || !desktopEnabled())
    return (pushActive = false);
  try {
    const registration = await worker();
    if (!registration) return (pushActive = false);
    const { public_key } = await api.get<{ public_key: string | null }>("/notifications/push-key");
    if (!public_key) return (pushActive = false);
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(public_key),
      }));
    await api.post("/notifications/push-subscriptions", subscription.toJSON());
    return (pushActive = true);
  } catch {
    return (pushActive = false);
  }
}

/** Ask for permission (must follow a tap or click), then subscribe. */
export async function enableNotifications(api: Api): Promise<NotificationPermission | "unsupported"> {
  if (permission() === "unsupported") return "unsupported";
  const answer = await Notification.requestPermission();
  if (answer === "granted") {
    setDesktopEnabled(true);
    await syncPush(api);
  }
  return answer;
}

/** Stop push to this device: on sign out (so the next person to use it doesn't get yours), or
    when turned off in settings. The browser's permission stays as it is. */
export async function disablePush(api: Api): Promise<void> {
  pushActive = false;
  try {
    const registration =
      "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : null;
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    await api
      .post("/notifications/push-subscriptions/remove", { endpoint: subscription.endpoint })
      .catch(() => {});
    await subscription.unsubscribe();
  } catch {
    /* nothing to undo */
  }
}

/** Whether Web Push covers this device now: the browser holds a push subscription for the app's
    service worker (the API was given it on sign-in or load, and sends every notification to it).
    Then the service worker shows each notification, and a tab mustn't show it too (UX-191). Asks
    the browser rather than trusting ``isPushActive``, which is false until ``syncPush`` finishes,
    or when it failed, while the subscription the API sends to is still live. */
export async function pushCoversDevice(registration: ServiceWorkerRegistration): Promise<boolean> {
  if (pushSupport() !== "push" || !desktopEnabled() || !registration.pushManager) return false;
  try {
    return (await registration.pushManager.getSubscription()) != null;
  } catch {
    return false;
  }
}

function shownIds(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(SHOWN_KEY) ?? "[]") as unknown;
    return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** Claim notification ``id`` for this tab: true the first time any tab of the app asks. */
export function claimNotification(id: string): boolean {
  const ids = shownIds();
  if (ids.includes(id)) return false;
  try {
    localStorage.setItem(SHOWN_KEY, JSON.stringify([...ids, id].slice(-SHOWN_KEEP)));
  } catch {
    /* no storage: this tab shows it */
  }
  return true;
}

/** Run ``show`` for notification ``id`` in only one of this app's tabs on this device. Every open
    tab gets it on its live stream at the same moment; a Web Lock (where the browser has them)
    makes them take turns, so the second sees the first's claim (UX-191). */
export function showOnce(id: string, show: () => void | Promise<void>): Promise<void> {
  const run = async () => {
    if (claimNotification(id)) await show();
  };
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request)
    return locks.request(`corecrm-notification-${id}`, run).then(
      () => {},
      () => {},
    );
  return run().catch(() => {});
}
